/**
 * The environment as a system: the record "Explore my repos" writes, Refresh
 * keeps, Claim reads and the customer sees (spec 2026-10-14 §3, contract
 * `docs/superpowers/plans/2026-10-19-explore-claim-fletcher-overnight.md`).
 *
 * One environment per Team. It holds the components that make up the
 * customer's system, the needs only a person can answer, the scripts that
 * bring the copy up and prove it healthy, and the base snapshot claims clone
 * from. Every list here is closed: the stores refuse a value that is not on
 * it, and the Explore brief names the same lists so the model cannot invent
 * a cause code.
 *
 * Nothing in this record is a secret. Needs carry names, never values; a
 * provided credential lives in a Fletcher environment revision's secret bag
 * and the need points at that revision.
 */
import { z } from "zod";
/** The three honest end states of a system. Never a hang, never a vague failure. */
export const SYSTEM_STATES = ["working", "hybrid", "could_not_run"];
/** What the environment is doing right now; `idle` between Explore and Refresh runs. */
export const SYSTEM_ACTIVITIES = ["idle", "exploring", "refreshing"];
/**
 * Why a system, a component or a claim did not reach Working. Closed: the
 * Explore brief names this list and the store refuses anything else. The
 * validation's runs invented `needs_external_service` and `payments_missing`;
 * those read as `third_party_service` and `build_failed` here.
 */
export const SYSTEM_CAUSE_CODES = [
    "system_too_large",
    "credential_missing",
    "test_data_missing",
    "reach_missing",
    "capability_missing",
    "third_party_service",
    "toolchain_unreachable",
    "build_failed",
    "start_failed",
    "health_failed",
    "seed_failed",
    "sign_in_failed",
    "dependency_cycle",
    "substrate",
    "timeout",
];
/** Customer words for each cause, finishing "could not run: …". No em-dashes, never "container". */
export const SYSTEM_CAUSE_WORDS = {
    system_too_large: "the system needs more memory than one sandbox holds",
    credential_missing: "it needs a credential only you can give",
    test_data_missing: "it needs test data only you can give",
    reach_missing: "it needs access to a service outside the copy",
    capability_missing: "YAGNI cannot connect to a service it needs yet",
    third_party_service: "it is a third-party service, so it stays remote",
    toolchain_unreachable: "a tool it needs could not be installed",
    build_failed: "it did not build",
    start_failed: "it built but did not start",
    health_failed: "it started but did not answer its health check",
    seed_failed: "its test data could not be loaded",
    sign_in_failed: "the test sign-in did not work",
    dependency_cycle: "its parts depend on each other in a loop",
    substrate: "the sandbox's machine failed on our side",
    timeout: "it ran out of time",
};
export const COMPONENT_KINDS = [
    "deployable",
    "service",
    "library",
    "infrastructure",
    "archived",
];
export const COMPONENT_SOURCE_KINDS = ["build", "image", "remote"];
/**
 * The checklist pills (Not set up, Setting up, Needs you, Working, Could not
 * run) plus `remote`: a component that runs on the customer's side and is a
 * named edge of a hybrid system.
 */
export const COMPONENT_STATES = [
    "not_set_up",
    "setting_up",
    "needs_you",
    "working",
    "remote",
    "could_not_run",
];
/** The sign-in modes a component can use; the QA environment's modes minus the retired aliases. */
export const SYSTEM_SIGN_IN_MODES = [
    "public",
    "command",
    "human",
    "cognito",
];
export const SEED_KINDS = ["script", "fixture", "generated", "dump"];
export const MEMORY_SOURCES = ["manifest", "observed", "estimate"];
/** The scripts an environment keeps. Each is proven from a fresh login shell before it counts. */
export const SYSTEM_SCRIPT_NAMES = ["up.sh", "health.sh", "tour.cjs"];
export const SYSTEM_SCRIPT_MAX_BYTES = 262_144;
export const NEED_KINDS = ["credential", "test_data", "reach", "capability"];
export const NEED_STATUSES = ["open", "provided", "declined"];
/**
 * The aggregation key for a `capability` need (spec §10). Capability needs are
 * counted across workspaces by this key, never by customer, and filed under
 * the Linear project "Platform and reliability".
 */
export const CAPABILITY_KEYS = [
    "logs",
    "errors",
    "tracker",
    "ci",
    "feature_flags",
    "registry",
    "identity_provider",
    "queue",
    "search",
    "machine_size",
    "other",
];
/** How a claim ended. `recorded` means Fletcher's evidence (the video) exists. */
export const CLAIM_OUTCOMES = ["recorded", "could_not_run", "cancelled", "expired"];
/**
 * A claim's hard cap, fixed at its start: its deadline never lies past
 * `startedAt` plus this, however often the lease is renewed. Two claims and a
 * base rebuild fit inside Fletcher's 90-minute run deadline.
 */
export const SYSTEM_CLAIM_HARD_CAP_MS = 40 * 60_000;
/**
 * How far ahead of a renewal a claim's deadline rolls (never past the hard
 * cap). A holder that keeps renewing is not reaped mid-run by its deadline;
 * one that stops is reaped by its lease.
 */
export const SYSTEM_CLAIM_DEADLINE_EXTENSION_MS = 10 * 60_000;
/** The default memory ceiling (performance-16x) and the one larger setting. */
export const DEFAULT_MEMORY_CEILING_MB = 65_536;
export const LARGE_MEMORY_CEILING_MB = 131_072;
export const MEMORY_CEILINGS_MB = [DEFAULT_MEMORY_CEILING_MB, LARGE_MEMORY_CEILING_MB];
/** The memory ceiling a workspace's stored setting names; unset or unknown is the default. */
export function memoryCeilingMb(setting) {
    return setting === LARGE_MEMORY_CEILING_MB ? LARGE_MEMORY_CEILING_MB : DEFAULT_MEMORY_CEILING_MB;
}
const COMPONENT_KEY = /^[a-z0-9][a-z0-9._-]{0,62}$/;
export function isComponentKey(value) {
    return typeof value === "string" && COMPONENT_KEY.test(value);
}
const shortText = z.string().trim().min(1).max(500);
/**
 * One line of what a map line came from. `file` names the file it was read
 * from (`line` may be null for a whole file). An estimate has no file behind
 * it: `file` is null (and so is `line`), and the claim says what the estimate
 * rests on. A component whose evidence names no file is shown as an estimate.
 */
export const componentEvidenceSchema = z
    .object({
    file: z.string().trim().min(1).max(500).nullable(),
    line: z.number().int().positive().nullable(),
    claim: shortText,
})
    .refine((evidence) => evidence.file !== null || evidence.line === null, {
    message: "an estimate with no file has no line",
    path: ["line"],
});
export const componentSourceSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("build"),
        /** `owner/name` of the repository the component builds from. */
        repo: z.string().trim().min(3).max(200),
        path: z.string().max(500).optional(),
        dockerfile: z.string().max(500).optional(),
        target: z.string().max(200).optional(),
    }),
    z.object({
        kind: z.literal("image"),
        image: z.string().trim().min(1).max(500),
        tagFrom: z.enum(["commit", "branch", "fixed"]),
        ref: z.string().max(200),
    }),
    z.object({
        kind: z.literal("remote"),
        url: z.string().url().max(2000),
        signInMode: z.enum(SYSTEM_SIGN_IN_MODES).optional(),
        /** Names the workspace variable or need holding the test identity; never the value. */
        identityRef: z.string().max(200).optional(),
    }),
]);
export const componentWiringSchema = z.object({
    env: z
        .string()
        .regex(/^[A-Za-z_][A-Za-z0-9_]*$/)
        .max(200),
    from: z.string().trim().min(1).max(200),
});
/**
 * How a component runs, as Explore's `map.json` states it: its start
 * command, the port it listens on, its readiness check (an HTTP path or
 * `tcp`) and the components it starts after, by key. Recorded, never run
 * from here; `up.sh` stays the proven way to start the system.
 */
export const componentRuntimeSchema = z.object({
    start: z.string().trim().min(1).max(2000).nullable(),
    port: z.number().int().min(1).max(65535).nullable(),
    readiness: z.string().trim().min(1).max(500).nullable(),
    dependsOn: z.array(z.string().regex(COMPONENT_KEY)).max(100),
});
export const seedRecipeSchema = z.object({
    kind: z.enum(SEED_KINDS),
    ref: z.string().trim().min(1).max(500),
});
export const signInSchema = z.object({
    mode: z.enum(SYSTEM_SIGN_IN_MODES),
    ref: z.string().max(500).optional(),
});
export const CORRECTABLE_COMPONENT_FIELDS = [
    "kind",
    "source",
    "wiring",
    "seed",
    "signIn",
    "memoryMb",
    "memorySource",
];
export const componentCorrectionSchema = z.object({
    field: z.enum(CORRECTABLE_COMPONENT_FIELDS),
    by: z.string().min(1).max(200),
    at: z.string().datetime(),
    to: z.unknown(),
});
/**
 * A line saying how the copy differs from what the customer's own build
 * makes, e.g. "built @acme/ui from the sibling repository at 1a2b3c4,
 * lockfile pins 9f8e7d6". Shown to the customer as written.
 */
export const provenanceLineSchema = z.object({
    componentKey: z.string().regex(COMPONENT_KEY).nullable(),
    kind: z.enum(["sibling_build", "repair", "generated_secret", "stand_in", "note"]),
    text: z.string().trim().min(1).max(1000),
    at: z.string().datetime(),
});
/**
 * What a claim clones. At 16 GB or less a suspended clone; above it a volume
 * fork into a fresh machine. Credentials are never in it.
 */
export const baseSnapshotSchema = z.object({
    goldenImage: z.string().trim().min(1).max(500),
    mode: z.enum(["suspended_clone", "volume_fork"]),
    machineId: z.string().max(200).nullable(),
    volumeId: z.string().max(200).nullable(),
    snapshotId: z.string().max(200).nullable(),
    memoryMb: z.number().int().positive(),
    takenAt: z.string().datetime(),
    /**
     * The egress mode Refresh made the suspended clone under (ADR-0053). A claim
     * resumes the clone only under the same mode. Read only for a base naming a
     * suspended clone, which only Refresh makes; absent on one recorded before
     * Refresh kept the mode, whose clone was made listed.
     */
    egressMode: z.enum(["open", "listed"]).optional(),
});
export const claimCoverageSchema = z.object({
    working: z.array(z.string().regex(COMPONENT_KEY)),
    remote: z.array(z.string().regex(COMPONENT_KEY)),
    couldNotRun: z.array(z.string().regex(COMPONENT_KEY)),
    /** Every component the pull request touched, rebuilt in the copy. Absent on claims recorded before it existed. */
    touched: z.array(z.string().regex(COMPONENT_KEY)).optional(),
});
/**
 * `team`: the per-Team environment of the overnight build. `workspace`: the
 * System, one per workspace and owned by it, with no Team (spec 2026-10-19
 * section 1). A per-Team environment folded into the System names it in
 * `supersededBy`.
 */
export const SYSTEM_SCOPES = ["team", "workspace"];
function member(list) {
    const set = new Set(list);
    return (value) => typeof value === "string" && set.has(value);
}
export const isSystemState = member(SYSTEM_STATES);
export const isSystemCauseCode = member(SYSTEM_CAUSE_CODES);
export const isComponentState = member(COMPONENT_STATES);
export const isNeedKind = member(NEED_KINDS);
export const isCapabilityKey = member(CAPABILITY_KEYS);
export const isClaimOutcome = member(CLAIM_OUTCOMES);
export const isSystemScriptName = member(SYSTEM_SCRIPT_NAMES);
/** Components that must run for the system to be Working; libraries, infrastructure and archived repositories never do. */
function runs(component) {
    return component.kind === "deployable" || component.kind === "service";
}
/**
 * The system's state from its components' states, the one rule every stream
 * uses:
 * - every deployable and service Working: `working`;
 * - at least one deployable Working and the rest remote, needing a person or
 *   could not run: `hybrid`, with the cause of the first edge that is not
 *   Working;
 * - no deployable Working: `could_not_run`, with the first component's cause.
 * While a running component is still not set up or setting up, or when the map
 * has nothing that runs, there is no state yet (`null`).
 */
export function deriveSystemState(components) {
    const running = components.filter(runs);
    if (running.length === 0)
        return null;
    if (running.some((c) => c.state === "not_set_up" || c.state === "setting_up"))
        return null;
    const notWorking = running.filter((c) => c.state !== "working");
    if (notWorking.length === 0)
        return { state: "working", causeCode: null };
    // Needs you, remote and could not run always name a cause (the store and
    // the table refuse one without); `start_failed` only guards a row that
    // somehow has none.
    const causeCode = notWorking.find((c) => c.causeCode)?.causeCode ?? "start_failed";
    const deployableWorking = running.some((c) => c.kind === "deployable" && c.state === "working");
    return { state: deployableWorking ? "hybrid" : "could_not_run", causeCode };
}
//# sourceMappingURL=systemEnvironment.js.map